/**
 * Router mínimo sobre la History API.
 *
 * Escrito a mano y no con una librería: la app tiene nueve pantallas y ningún parámetro
 * complicado, y una dependencia de router traería su propio modelo de datos, su propio
 * ciclo de vida y una versión mayor que romper. Son cincuenta líneas.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type Pantalla =
  | 'inicio'
  | 'registro'
  | 'historial'
  | 'cuerpo'
  | 'calendario'
  | 'ingesta'
  | 'nutricion'
  | 'progreso'
  | 'ajustes';

/**
 * El título de acá es EL nombre de la pantalla: lo usan la cabecera de las dos variantes, el tab
 * y el encabezado de la ayuda. `registro` se llama "Entrenar" y no "Registrar" porque el tab
 * siempre dijo Entrenar: con dos nombres para la misma pantalla, la ayuda hablaba de una pantalla
 * que en el título no existía. La ruta sigue siendo `/registro` — cambiarla rompería los enlaces
 * que la gente ya tiene guardados.
 */
export const PANTALLAS: Record<Pantalla, { ruta: string; titulo: string }> = {
  inicio: { ruta: '/', titulo: 'Inicio' },
  registro: { ruta: '/registro', titulo: 'Entrenar' },
  historial: { ruta: '/historial', titulo: 'Historial' },
  cuerpo: { ruta: '/cuerpo', titulo: 'Cuerpo' },
  calendario: { ruta: '/calendario', titulo: 'Plan' },
  ingesta: { ruta: '/foto', titulo: 'Foto' },
  nutricion: { ruta: '/comida', titulo: 'Comida' },
  progreso: { ruta: '/progreso', titulo: 'Progreso' },
  ajustes: { ruta: '/ajustes', titulo: 'Ajustes' },
};

export interface Ubicacion {
  pantalla: Pantalla;
  /** Segmento extra: /progreso/ej-chest-press → 'ej-chest-press'. */
  detalle: string | null;
  busqueda: URLSearchParams;
}

function parsear(url: string): Ubicacion {
  const u = new URL(url, window.location.origin);
  const partes = u.pathname.split('/').filter(Boolean);
  const primera = `/${partes[0] ?? ''}`;

  const encontrada = (Object.entries(PANTALLAS) as [Pantalla, { ruta: string }][]).find(
    ([, v]) => v.ruta === primera || (v.ruta === '/' && partes.length === 0),
  );

  return {
    pantalla: encontrada?.[0] ?? 'inicio',
    detalle: partes[1] ?? null,
    busqueda: u.searchParams,
  };
}

interface Contexto extends Ubicacion {
  ir: (pantalla: Pantalla, detalle?: string) => void;
  volver: () => void;
}

const Ctx = createContext<Contexto | null>(null);

export function ProveedorRuta({ children }: { children: ReactNode }): ReactNode {
  const [href, setHref] = useState(() => window.location.href);

  useEffect(() => {
    const alVolver = () => setHref(window.location.href);
    window.addEventListener('popstate', alVolver);
    return () => window.removeEventListener('popstate', alVolver);
  }, []);

  const ir = useCallback((pantalla: Pantalla, detalle?: string) => {
    const base = PANTALLAS[pantalla].ruta;
    const ruta = detalle ? `${base === '/' ? '' : base}/${detalle}` : base;
    window.history.pushState(null, '', ruta);
    setHref(window.location.href);
    // Cambiar de pantalla vuelve arriba: heredar el scroll de la anterior desorienta.
    window.scrollTo(0, 0);
  }, []);

  const volver = useCallback(() => window.history.back(), []);

  const valor = useMemo<Contexto>(() => ({ ...parsear(href), ir, volver }), [href, ir, volver]);
  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useRuta(): Contexto {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useRuta necesita estar dentro de <ProveedorRuta>.');
  return ctx;
}
